import { ProductPrice } from "@/api/product-api";
import { isReferralActiveForProduct } from "@/libs/referral/referral-items";

/**
 * referral 이 반영된 **단가 계산** (#5110).
 *
 * ⚠️ **산식은 표시가 정본(`ProductUtil.getPrice`)과 반드시 같아야 한다.** #5110 의 증상이
 * 정확히 "카트에 보이는 값과 청구액이 다르다" 였고, 고치면서 반올림 시점이나 중첩 여부가
 * 한 줄이라도 갈리면 같은 증상이 재발한다.
 *
 * ### 왜 별도 모듈인가
 *
 * 한때 이 계산이 `api/naverpay/register.ts` 의 `resolveItemPrice` 안에 카탈로그 조회와
 * **한 덩어리로** 있었다. 그래서 동등성 테스트가 함수를 부르지 못하고 **산식을 복사해서**
 * 비교했는데, 그러면 `register.ts` 만 고쳤을 때 테스트는 초록인 채로 실제 청구액만
 * 갈라진다 — 이 테스트가 막으려던 바로 그 실패다. 순수 계산을 떼어 **테스트가 실물을
 * 호출**하게 한다.
 *
 * 조회(어떤 상품의 KRW 가격인가)는 여전히 `register.ts` 가 한다. 여기는 값만 받는다.
 */

export interface ReferralPricedAmounts {
  /** 사용자 결제가 = originalAmount − discountAmount */
  salePrice: number;
  /** admin baseline 가격 (= `OrderItemV2.price`) */
  originalAmount: number;
  /** MSRP, line-through 표시용 (= `OrderItemV2.displayPrice`) */
  displayAmount: number;
  /** 카탈로그 할인 + referral 할인 (= `OrderItemV2.discount`) */
  discountAmount: number;
}

/**
 * @param slug 상품 슬러그. referral 적용 대상인지 판정에 쓴다(대상 밖이면 카탈로그가 그대로).
 * @param krwPrice 카탈로그 KRW 가격. **클라이언트가 보낸 값이 아니라 서버 조회 결과**여야 한다.
 * @param referralPercentile 쿠키에서 파싱·클램프된 값. 0 이면 referral 없음.
 */
export function priceWithReferral({
  slug,
  krwPrice,
  referralPercentile,
}: {
  slug: string;
  krwPrice: ProductPrice;
  referralPercentile: number;
}): ReferralPricedAmounts {
  const original = krwPrice.originalAmount ?? 0;
  const catalogDiscount = krwPrice.discountAmount ?? 0;
  // displayAmount 가 비어 있으면 originalAmount 로 fallback (`OrderService` 와 동일 패턴).
  const display = krwPrice.displayAmount || original;

  // referral 할인은 카탈로그 할인 **위에 중첩**된다 — `ProductUtil.getPrice` 의
  // `discountAmount + round((original − discount) × percentile)` 과 같은 순서·반올림.
  const referralDiscount =
    referralPercentile > 0 && isReferralActiveForProduct(slug)
      ? Math.round((original - catalogDiscount) * referralPercentile)
      : 0;
  const discount = catalogDiscount + referralDiscount;

  return {
    // ⚠️ 음수 클램프. 쿠키는 무서명이라 위조된 percentile 이 여기까지 올 수 있고,
    // 파서 클램프가 뚫리면 이 줄이 마지막 방어선이다.
    salePrice: Math.max(0, original - discount),
    originalAmount: original,
    displayAmount: display,
    discountAmount: discount,
  };
}
